import React, { useContext, useState } from "react";
import { NavLink, Outlet } from 'react-router-dom';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import MenuIcon from '@mui/icons-material/Menu';
import {
  UserContext,
  useApiBase
} from "m34n-ui";

const adminNavLinks = [
  { label: 'Scheduler', path: '/admin' },
  { label: 'Visual Schedule', path: '/admin/schedules/visual' },
  { label: 'Email Templates', path: '/admin/email/templates' },
  { label: 'Waitlist', path: '/admin/waitlist' }
];

const publicNavLinks = [
  { label: 'Book', path: '/book' },
  { label: 'Landing', path: '/' }
];

const drawerWidth = 240;

const AdminLayout = () => {
  const { user } = useContext(UserContext);
  const api = useApiBase();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await api.user.logout();
    window.location.reload();
  };

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleNavItemClick = () => {
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const renderNavList = (items) => (
    items.map((item) => (
      <ListItem key={item.path} disablePadding sx={{ px: 1 }}>
        <ListItemButton
          component={NavLink}
          to={item.path}
          end={item.path === '/admin'}
          onClick={handleNavItemClick}
          sx={{
            borderRadius: 1,
            '&.active': {
              backgroundColor: 'rgba(60,31,255,0.12)',
              color: 'primary.main',
            },
          }}
        >
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{ fontWeight: 600 }}
          />
        </ListItemButton>
      </ListItem>
    ))
  );

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ px: 2 }}>
        <Typography variant="h6">
          Pluto TV • Rage Room
        </Typography>
      </Toolbar>
      <Divider />
      <Box sx={{ flexGrow: 1, py: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <List
          sx={{ p: 0 }}
          subheader={
            <ListSubheader component="div" sx={{ lineHeight: 2, fontWeight: 600, color: 'text.secondary' }}>
              Admin
            </ListSubheader>
          }
        >
          {renderNavList(adminNavLinks)}
        </List>
        <Divider flexItem />
        <List
          sx={{ p: 0 }}
          subheader={
            <ListSubheader component="div" sx={{ lineHeight: 2, fontWeight: 600, color: 'text.secondary' }}>
              Public
            </ListSubheader>
          }
        >
          {renderNavList(publicNavLinks)}
        </List>
      </Box>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {user?.email && (
          <Chip
            label={user.email}
            size="small"
            sx={{
              backgroundColor: 'rgba(60,31,255,0.18)',
              border: '1px solid rgba(244,243,248,0.16)',
            }}
          />
        )}
        <Button variant="outlined" color="secondary" onClick={handleLogout}>
          Logout
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="admin navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid rgba(244,243,248,0.12)',
              backgroundColor: 'background.paper',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid rgba(244,243,248,0.12)',
              backgroundColor: 'background.paper',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{ flexGrow: 1, width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: { xs: 3, md: 4 },
            }}
          >
            <IconButton
              color="primary"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              Pluto TV • Rage Room Admin
            </Typography>
          </Box>
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
};

export default AdminLayout;